<script setup>
// The pass's own navigation drawer (right side panel) — a faithful replacement
// for the app's suppressed global chrome, with full parity to the Footer tab
// bar + More page (same store gates, routes and badges). The visible top bar
// was removed: the giveaway header (index.vue) now owns the brand logo + the
// hamburger, which opens this drawer via `v-model:open`.
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import {
  ChevronLeft,
  CircleHelp,
  Compass,
  Gift,
  History,
  LifeBuoy,
  Lightbulb,
  ListChecks,
  LogOut,
  MessageCircle,
  MessageSquare,
  Phone,
  ShoppingBag,
  Ticket,
  Trophy,
  X
} from '@lucide/vue'
import api from '@/api'
import {
  initializeIntercom,
  isIntercomAvailable,
  openIntercom
} from '@/utils/intercom'
import { useFrTokens } from '../composables/useFrTokens'
import SuggestFeatureSheet from './SuggestFeatureSheet.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const frTokens = useFrTokens()
// Drawer open state is owned by the host header (the hamburger lives there now).
const drawerOpen = defineModel('open', { type: Boolean, default: false })
const suggestOpen = ref(false)

const props = defineProps({
  /** Brand logo url override for the drawer head (else HorizontalLogo media). */
  logo: { type: String, default: null }
})

// Suggest-a-feature (absorbed from the legacy More page) opens from the drawer.
function openSuggest() {
  drawerOpen.value = false
  suggestOpen.value = true
}

// Logo url: explicit prop override, else offer/partner HorizontalLogo media.
const logoUrl = computed(() => {
  if (props.logo) return props.logo
  const find = media => media?.find(m => m.location === 'HorizontalLogo' && m.url)?.url
  return find(store.state.offer?.media) || find(store.state.partner?.media) || null
})
const destinationName = computed(() => store.state.partner?.name || 'Experiences')

// Same store sources as Footer/index.vue.
const offer = computed(() => store.state.offer)
const partner = computed(() => store.state.partner)
const partnerUser = computed(() => store.state.partnerUser)
const hasGamifiedDealsWithOrders = computed(
  () => store.getters['deals/hasGamifiedDealsWithOrders']
)
const cartItemCount = computed(
  () => store.getters['cartOrders/getCartLength'] ?? 0
)
const unreadCount = computed(() => store.state.messages?.unread ?? 0)

// Full parity with the Footer tab list: same gates, same route names, same
// badges, plus Messages surfaced directly (it lives under More in the app).
const navItems = computed(() => {
  const items = [
    ...(partnerUser.value?.id
      ? [
          {
            // Footer parity: partner 942 renames the tab to "Check-In".
            label: partner.value?.id === 942 ? 'Check-In' : 'Experiences',
            icon: Ticket,
            name: 'Experiences',
            id: 'giveaway_drawer_experiences_btn'
          }
        ]
      : []),
    {
      label: 'Explore',
      icon: Compass,
      name: 'Explore',
      id: 'giveaway_drawer_explore_btn'
    },
    ...(hasGamifiedDealsWithOrders.value || offer.value?.use_points
      ? [
          {
            label: 'Rewards',
            icon: Gift,
            name: 'Rewards',
            id: 'giveaway_drawer_rewards_btn'
          }
        ]
      : []),
    {
      label: 'My Bag',
      icon: ShoppingBag,
      name: 'ReviewOrder',
      id: 'giveaway_drawer_bag_btn',
      badge: cartItemCount.value
    },
    ...(partnerUser.value?.id
      ? [
          {
            label: 'Messages',
            icon: MessageSquare,
            name: 'Messages',
            id: 'giveaway_drawer_messages_btn',
            badge: unreadCount.value
          }
        ]
      : []),
  ]

  return items.filter(item => router.hasRoute(item.name))
})

// Footer's isMatchingRoute: exact route name or a child route's meta.parent.
const isActive = item =>
  route.name === item.name || route.meta?.parent === item.name

// ---- The drawer fully absorbs the legacy More page (no More route). ----

// Identity header (More page parity: avatar initials + name + email | phone).
const initials = computed(() => {
  const u = partnerUser.value || {}
  const first = u.first_name || (u.name || '').split(' ')[0] || ''
  const last = u.last_name || (u.name || '').split(' ').slice(1).join(' ') || ''
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
})

// Account section (same gates as More's menuGroups).
const accountItems = computed(() => {
  if (!partnerUser.value?.id) return []
  const items = [
    { label: 'Order History', icon: History, name: 'OrderHistory', id: 'giveaway_drawer_orders_btn' },
    { label: 'Manage Subscriptions', icon: ListChecks, name: 'Subscriptions', id: 'giveaway_drawer_subscriptions_btn' },
    ...(hasGamifiedDealsWithOrders.value || offer.value?.use_points
      ? [{ label: 'Rewards History', icon: Trophy, name: 'RewardsHistory', id: 'giveaway_drawer_rewards_history_btn' }]
      : [])
  ]
  return items.filter(item => router.hasRoute(item.name))
})

// Help & support gates mirror views/More/index.vue.
const showSupport = computed(
  () => Boolean(partnerUser.value?.id) && isIntercomAvailable()
)
const supportPhone = computed(() => offer.value?.support_phone || '')
const supportText = computed(() => offer.value?.support_text || '')
const showFaqs = computed(() => router.hasRoute('CommonQuestions'))

function go(name) {
  drawerOpen.value = false
  router.push({ name, query: { t: window.token } })
}

function onSupportClick() {
  drawerOpen.value = false
  openIntercom()
}

// Mirrors More/index.vue logout exactly.
async function logout() {
  try {
    await api.auth.logout()
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
    window.location.href = `${base}/login?t=${window.token}`
  } catch (error) {
    console.error('Could not logout', error)
  }
}

// Same Intercom bootstrapping as More/index.vue's mounted hook, with the same
// store-backed payload. initializeIntercom no-ops without an app id, but it
// reads partnerUser.id unguarded — so only call it for a signed-in passholder.
onMounted(() => {
  if (!partnerUser.value?.id) return
  initializeIntercom({
    partnerUser: partnerUser.value,
    partner: partner.value,
    deals: store.getters['deals/list'] || [],
    standaloneProducts: store.getters['products/standalone'] || []
  })
})
</script>

<template>
  <!-- Side drawer (right panel, per source) — opened by the header hamburger -->
  <Teleport to="body">
    <div v-if="drawerOpen" class="dg-drawer" data-overlay>
      <button type="button" aria-label="Close menu" class="dg-drawer__scrim" @click="drawerOpen = false" />
      <div class="dg-drawer__panel">
        <div class="dg-drawer__head">
          <img v-if="logoUrl" :src="logoUrl" :alt="destinationName" class="dg-drawer__wordmark" />
          <span v-else class="dg-topbar__wordmark-text">{{ destinationName }}</span>
          <button
            id="giveaway_drawer_close_btn"
            type="button"
            aria-label="Close menu"
            class="dg-drawer__close gtm-click"
            @click="drawerOpen = false"
          >
            <X :size="16" />
          </button>
        </div>

        <!-- Identity (absorbed from the legacy More page) -->
        <div v-if="partnerUser?.id" class="dg-drawer__identity">
          <span class="dg-drawer__avatar">{{ initials }}</span>
          <div class="dg-drawer__identity-text">
            <div class="dg-drawer__identity-name">{{ partnerUser.name }}</div>
            <div v-if="partnerUser.email || partnerUser.phone" class="dg-drawer__identity-contact"><span v-if="partnerUser.email">{{ partnerUser.email }}</span><span v-if="partnerUser.phone">{{ partnerUser.phone }}</span></div>
          </div>
        </div>

        <nav class="dg-drawer__nav" data-scrollable>
          <button
            v-for="item in navItems"
            :id="item.id"
            :key="item.name"
            type="button"
            class="dg-drawer__item gtm-click"
            :class="{ 'is-active': isActive(item) }"
            @click="go(item.name)"
          >
            <span class="dg-drawer__item-icon"><component :is="item.icon" :size="18" /></span>
            <span>{{ item.label }}</span>
            <span v-if="item.badge > 0" class="dg-drawer__item-badge">{{ item.badge }}</span>
            <span v-if="isActive(item)" class="dg-drawer__item-dot" />
          </button>

          <!-- Account (absorbed from the legacy More page) -->
          <template v-if="accountItems.length">
            <div class="dg-drawer__section-label">Account</div>
            <button
              v-for="item in accountItems"
              :id="item.id"
              :key="item.name"
              type="button"
              class="dg-drawer__item gtm-click"
              :class="{ 'is-active': isActive(item) }"
              @click="go(item.name)"
            >
              <span class="dg-drawer__item-icon"><component :is="item.icon" :size="18" /></span>
              <span>{{ item.label }}</span>
            </button>
          </template>

          <!-- Need help? (absorbed from the legacy More page) -->
          <template v-if="showSupport || supportPhone || supportText || showFaqs">
            <div class="dg-drawer__section-label">Need help?</div>
            <button
              v-if="showSupport"
              id="giveaway_drawer_support_btn"
              type="button"
              class="dg-drawer__item gtm-click"
              @click="onSupportClick"
            >
              <span class="dg-drawer__item-icon"><LifeBuoy :size="18" /></span>
              <span>Request Support</span>
            </button>
            <button
              v-if="showFaqs"
              id="giveaway_drawer_faqs_btn"
              type="button"
              class="dg-drawer__item gtm-click"
              @click="go('CommonQuestions')"
            >
              <span class="dg-drawer__item-icon"><CircleHelp :size="18" /></span>
              <span>Common Questions</span>
            </button>
            <a
              v-if="supportPhone"
              id="giveaway_drawer_call_support_lnk"
              class="dg-drawer__item gtm-click"
              :href="`tel:${supportPhone}`"
            >
              <span class="dg-drawer__item-icon"><Phone :size="18" /></span>
              <span>Call support</span>
            </a>
            <a
              v-if="supportText"
              id="giveaway_drawer_text_support_lnk"
              class="dg-drawer__item gtm-click"
              :href="`sms:${supportText}`"
            >
              <span class="dg-drawer__item-icon"><MessageCircle :size="18" /></span>
              <span>Text support</span>
            </a>
          </template>

          <!-- Feedback (absorbed from the legacy More page's App Suggestions) -->
          <div class="dg-drawer__section-label">Feedback</div>
          <button
            id="giveaway_drawer_suggest_btn"
            type="button"
            class="dg-drawer__item gtm-click"
            @click="openSuggest"
          >
            <span class="dg-drawer__item-icon"><Lightbulb :size="18" /></span>
            <span>Suggest a feature</span>
          </button>
        </nav>

        <div class="dg-drawer__foot">
          <button
            id="giveaway_drawer_back_btn"
            type="button"
            class="dg-drawer__item gtm-click"
            @click="go('Experiences')"
          >
            <span class="dg-drawer__item-icon"><ChevronLeft :size="18" /></span>
            <span>Back to Experiences</span>
          </button>
          <button
            v-if="partnerUser?.id"
            id="giveaway_drawer_signout_btn"
            type="button"
            class="dg-drawer__item gtm-click"
            @click="logout"
          >
            <span class="dg-drawer__item-icon"><LogOut :size="18" /></span>
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Suggest-a-feature sheet (new-design absorption of the legacy modal) -->
  <SuggestFeatureSheet :open="suggestOpen" :theme="frTokens" @close="suggestOpen = false" />
</template>

<style lang="scss" scoped>
/* Drawer head fallback when a partner has no HorizontalLogo image. */
.dg-topbar__wordmark-text {
  font-family: var(--fr-font-serif-display, Georgia, serif);
  font-size: 15px;
  font-weight: 700;
}

/* Drawer — clamped to the pass column like every other overlay */
.dg-drawer {
  position: fixed;
  inset: 0;
  z-index: 2700;
}
.dg-drawer__scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  animation: dg-drawer-fade 150ms ease;
}
/* Source: `absolute right-0 top-0 h-full w-[78%] bg-white shadow-2xl slide-in-from-right` —
   width relative to the pass column, pinned to its right edge on wide screens. */
.dg-drawer__panel {
  position: absolute;
  top: 0;
  right: max(0px, calc((100vw - var(--project-max-width, 480px)) / 2));
  height: 100%;
  width: min(78%, calc(var(--project-max-width, 480px) * 0.78));
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: dg-drawer-slide 200ms ease;
}
/* Source: head `border-b border-black/10 px-4 pt-10 pb-3` */
.dg-drawer__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 40px 16px 12px;
}
.dg-drawer__wordmark {
  height: 20px;
  width: auto;
  max-width: 70%;
  object-fit: contain;
}
.dg-drawer__close {
  display: grid;
  height: 44px;
  width: 44px;
  margin: -6px -10px -6px 0;
  place-items: center;
  border: 0;
  background: none;
  border-radius: 999px;
  color: rgba(28, 28, 30, 0.7);
  cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.05); }
}
.dg-drawer__nav {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}
/* Source DrawerItem: `flex w-full items-center gap-3 px-4 py-3 text-sm font-medium` */
.dg-drawer__item {
  display: flex;
  width: 100%;
  min-height: 44px;
  align-items: center;
  gap: 12px;
  border: 0;
  background: none;
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: rgba(28, 28, 30, 0.8);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(0, 0, 0, 0.05); }
  &.is-active {
    background: rgba(var(--fr-primary-rgb, 255, 153, 0), 0.1);
    color: var(--fr-primary, #ff9900);
    .dg-drawer__item-icon { color: var(--fr-primary, #ff9900); }
  }
}
.dg-drawer__item-icon { color: rgba(28, 28, 30, 0.6); display: grid; place-items: center; }
.dg-drawer__item-badge {
  margin-left: auto;
  display: grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--fr-primary, #ff9900);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}
.dg-drawer__item-dot {
  margin-left: auto;
  height: 6px;
  width: 6px;
  border-radius: 999px;
  background: var(--fr-primary, #ff9900);
}
/* When a badge and the active dot coexist, the badge owns the auto margin. */
.dg-drawer__item-badge + .dg-drawer__item-dot {
  margin-left: 8px;
}
.dg-drawer__divider {
  height: 1px;
  margin: 8px 16px;
  background: rgba(0, 0, 0, 0.1);
}
/* Source foot: `mt-auto border-t border-black/10 p-3` */
.dg-drawer__foot {
  margin-top: auto;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 12px;
}

/* Identity header (absorbed from the legacy More page, new design system) */
.dg-drawer__identity {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.dg-drawer__avatar {
  display: grid;
  place-items: center;
  height: 44px;
  width: 44px;
  flex-shrink: 0;
  border-radius: 999px;
  background: var(--fr-primary, #ff9900);
  color: var(--fr-accent, #232f3e);
  font-size: 15px;
  font-weight: 800;
}
.dg-drawer__identity-text { min-width: 0; }
.dg-drawer__identity-name {
  font-family: var(--fr-font-serif-display, Georgia, serif);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--fr-ink, #1c1c1e);
}
.dg-drawer__identity-contact {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 11px;
  color: var(--fr-muted, #6b7280);
  overflow-wrap: anywhere;
}

/* Section labels for the absorbed Account / Need help groups */
.dg-drawer__section-label {
  margin-top: 10px;
  padding: 8px 16px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--fr-muted, #6b7280);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

/* The absorbed sections make the nav taller than short viewports */
.dg-drawer__nav {
  overflow-y: auto;
}

@keyframes dg-drawer-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes dg-drawer-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
</style>
